import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, signal } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { copyToClipboard } from '../../helpers/copy-to-clipboard.function';
import { IconComponent } from '../icon/icon.component';
import { NotyService } from '../noty/noty.service';

/** How long the button stays in the "copied" state before it turns back into a copy icon */
export const COPIED_STATE_DURATION_MS = 1500;

/**
 * A button that copies `value` to the clipboard, and says so: the icon becomes a check and the label
 * "Скопійовано" for a moment. On a phone it is an icon only, a 40px target. A failed copy is
 * reported in a toast, because a customer who thinks they copied an IBAN and did not pays nothing.
 */
@Component({
  selector: 'cmt-copy-button',
  templateUrl: './copy-button.component.html',
  styleUrls: ['./copy-button.component.scss'],
  imports: [IconComponent, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CopyButtonComponent {
  private notyService = inject(NotyService);
  private translateService = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  /** Exactly what lands in the clipboard */
  readonly value = input.required<string>();
  /** What is being copied, for screen readers: "Копіювати IBAN" */
  readonly subject = input<string>('');

  protected readonly isCopied = signal(false);

  private resetTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.destroyRef.onDestroy(() => this.clearResetTimeout());
  }

  protected async copy(): Promise<void> {
    if (!await copyToClipboard(this.value())) {
      this.notyService.error(this.translateService.instant('copy-button.failed'));
      return;
    }

    this.clearResetTimeout();
    this.isCopied.set(true);
    this.resetTimeoutId = setTimeout(() => this.isCopied.set(false), COPIED_STATE_DURATION_MS);
  }

  private clearResetTimeout(): void {
    if (this.resetTimeoutId !== null) {
      clearTimeout(this.resetTimeoutId);
      this.resetTimeoutId = null;
    }
  }
}
